import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section with Background */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        {/* Background Image - REPLACE: Search "software developer coding" or "business owner working" */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/70 to-blue-900/80 z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="mb-4">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold inline-block">
                Our Story
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-lg">
              Software Built Right<br />
              For Businesses That Work Hard
            </h1>
            <p className="text-xl drop-shadow-md">
              The story behind SuddyFlow and our mission to help field service 
              professionals succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6">
                Hey, I'm Jon—founder of SuddyFlow and owner of a pressure washing business 
                in the Lake Norman area of North Carolina. But before that, I spent over 20 years 
                as a professional software developer, building enterprise systems that companies 
                depend on every day.
              </p>

              <p className="text-lg text-gray-700 mb-6">
                When I started my service business, I thought managing customers and jobs would be 
                straightforward. I quickly learned that wasn't the case. I tried the "big name" CRM 
                platforms—you know the ones—but they were either too expensive, too complicated, or 
                charged ridiculous per-user fees that made growing my team financially painful.
              </p>

              <p className="text-lg text-gray-700 mb-6">
                So I did what any software developer with a service business would do—I built my own 
                solution. SuddyFlow started as my personal tool to manage my pressure washing company. 
                It helped me stay organized, never miss a follow-up, track my team, and run my operation 
                more professionally.
              </p>

              <p className="text-lg text-gray-700 mb-6">
                When I showed it to other field service business owners—pressure washers, landscapers, 
                window cleaners, and more—they all said, "I need this!" That's when I realized SuddyFlow 
                could help the entire outdoor service community.
              </p>
            </div>
          </div>

          {/* Mission Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            
            <p className="text-lg text-gray-700 mb-6">
              SuddyFlow exists to help field service professionals run their businesses like pros. 
              We believe you shouldn't need to be a tech expert or spend hours on administrative work. 
              Your focus should be on delivering great service and growing your business.
            </p>

            <p className="text-lg text-gray-700 mb-6">
              Every feature in SuddyFlow is designed with real-world field service needs in mind—because 
              it's built by someone who's out there doing the work, managing crews, and running a business 
              just like you.
            </p>
          </div>
        </div>
      </section>

      {/* Why We're Different - Visual Cards */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why We're Different</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Card 1 - Not Just Software Developers */}
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              {/* Background Image - REPLACE: Search "pressure washing business" or "outdoor service work" */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: 'url("https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?w=800&q=80")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.4)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-800/80 to-blue-900/70 z-0" />
              
              <div className="relative z-10 p-8 text-white">
                <div className="text-5xl mb-4">💼</div>
                <h3 className="text-2xl font-bold mb-4">Built by a Service Professional</h3>
                <p className="text-blue-100">
                  Unlike generic CRM tools built by people who've never worked outdoors, 
                  SuddyFlow understands your business because we ARE your business. We know what 
                  matters—job tracking, route optimization, weather considerations, seasonal 
                  fluctuations, and building repeat customers.
                </p>
              </div>
            </div>

            {/* Card 2 - Enterprise Experience */}
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              {/* Background Image - REPLACE: Search "software development" or "coding workspace" */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: 'url("https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.4)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/90 via-indigo-800/80 to-indigo-900/70 z-0" />
              
              <div className="relative z-10 p-8 text-white">
                <div className="text-5xl mb-4">💻</div>
                <h3 className="text-2xl font-bold mb-4">Enterprise-Grade Software</h3>
                <p className="text-blue-100">
                  With 20+ years building mission-critical software systems, I bring enterprise-level 
                  security, reliability, and best practices to field service management. Your data is 
                  protected with the same standards used by Fortune 500 companies.
                </p>
              </div>
            </div>

            {/* Card 3 - Built for Growth */}
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              {/* Background Image - REPLACE: Search "team collaboration" or "business growth" */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: 'url("https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.4)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/90 via-green-800/80 to-green-900/70 z-0" />
              
              <div className="relative z-10 p-8 text-white">
                <div className="text-5xl mb-4">📈</div>
                <h3 className="text-2xl font-bold mb-4">Designed for Growth</h3>
                <p className="text-green-100">
                  Start solo and scale to a full team without changing platforms or paying per-user fees. 
                  SuddyFlow grows with you—from your first customer to your hundredth employee.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Values</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-all">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Purpose-Built</h3>
              <p className="text-gray-700">
                Every feature is designed specifically for field service businesses. 
                No bloat, no unnecessary complexity—just what you need to succeed.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-lg border-2 border-green-200 hover:border-green-500 transition-all">
              <div className="text-5xl mb-4">🤝</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customer-Focused</h3>
              <p className="text-gray-700">
                We listen to our users and continuously improve based on real feedback 
                from service professionals across multiple industries.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg border-2 border-purple-200 hover:border-purple-500 transition-all">
              <div className="text-5xl mb-4">🔒</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Security & Reliability</h3>
              <p className="text-gray-700">
                Built with enterprise-grade security standards. Your business data, customer 
                information, and financial records are protected with bank-level encryption.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Background Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center">About the Founder</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            
            <div className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-md border-2 border-blue-200 hover:border-blue-500 transition-all">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-2xl">💻</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">20+ Years Software Development</h3>
                <p className="text-gray-700">
                  Built enterprise systems used by major corporations. Expertise in security, 
                  scalability, and creating software that businesses depend on daily.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-md border-2 border-green-200 hover:border-green-500 transition-all">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-2xl">💦</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Field Service Business Owner</h3>
                <p className="text-gray-700">
                  Runs an active pressure washing business in Lake Norman, NC. Understands the 
                  daily challenges of scheduling jobs, managing crews, and keeping customers happy.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-md border-2 border-red-200 hover:border-red-500 transition-all">
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-2xl">🎖️</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Military Veteran</h3>
                <p className="text-gray-700">
                  Values discipline, reliability, and attention to detail. Brings the same commitment 
                  to excellence learned in service to every aspect of SuddyFlow.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-md border-2 border-purple-200 hover:border-purple-500 transition-all">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-2xl">🌍</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Lake Norman Community</h3>
                <p className="text-gray-700">
                  Based in Mooresville, NC, serving the Lake Norman area. Building software that 
                  helps local businesses thrive and grow.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Technology & Security Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Built on Modern, Secure Technology
            </h2>
            <p className="text-xl text-gray-600">
              Enterprise-grade infrastructure you can trust
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg text-center">
              <div className="text-4xl mb-3">☁️</div>
              <h3 className="font-bold text-lg mb-2">Cloud-Based</h3>
              <p className="text-sm text-gray-700">Access from anywhere, automatic backups, always up-to-date</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg text-center">
              <div className="text-4xl mb-3">🔐</div>
              <h3 className="font-bold text-lg mb-2">Bank-Level Security</h3>
              <p className="text-sm text-gray-700">Encrypted data, secure payments, GDPR compliant</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg text-center">
              <div className="text-4xl mb-3">📱</div>
              <h3 className="font-bold text-lg mb-2">Mobile-First</h3>
              <p className="text-sm text-gray-700">Optimized for phone and tablet, works offline</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-lg text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-bold text-lg mb-2">Fast & Reliable</h3>
              <p className="text-sm text-gray-700">99.9% uptime, lightning-fast performance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Join Community CTA */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image - REPLACE: Search "team success" or "business handshake" */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-blue-900/80 z-0" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-6 drop-shadow-lg">
            Join the SuddyFlow Community
          </h2>
          <p className="text-xl mb-8 text-blue-100 drop-shadow-md">
            Built by a service professional who understands your business. 
            Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/register" 
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 shadow-lg transition-colors"
            >
              Start Free Trial
            </Link>
            <Link 
              to="/features" 
              className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 shadow-lg transition-colors"
            >
              Explore Features
            </Link>
          </div>
          <p className="mt-6 text-blue-200">
            14 days free • No credit card required • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;